@file:OptIn(ExperimentalUuidApi::class)

package com.siriusxm.pia.components

import androidx.compose.runtime.Composable
import androidx.compose.runtime.collectAsState
import androidx.compose.runtime.getValue
import com.siriusxm.cmc.Notification
import com.siriusxm.cmc.NotificationType
import com.siriusxm.cmc.Notifications
import com.siriusxm.pia.SXMUI
import kotlinx.coroutines.CoroutineScope
import kotlinx.coroutines.Job
import kotlinx.coroutines.delay
import kotlinx.coroutines.flow.MutableStateFlow
import kotlinx.coroutines.flow.StateFlow
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Text
import kotlin.concurrent.atomics.AtomicLong
import kotlin.concurrent.atomics.ExperimentalAtomicApi
import kotlin.time.Duration
import kotlin.time.Duration.Companion.milliseconds
import kotlin.uuid.ExperimentalUuidApi
import kotlin.uuid.Uuid


/**
 * Manages all notifications, and provides an API to post new notifications.
 */
class NotificationCenter(
    private val scope: CoroutineScope
) : Notifications {
    @OptIn(ExperimentalAtomicApi::class)
    private val nextId = AtomicLong(1L)
    private val _items = MutableStateFlow<List<Notification>>(emptyList())
    val items: StateFlow<List<Notification>> = _items

    // Pending auto-dismiss timers by notification id. Replacing a notification must cancel the old
    // timer, or it would dismiss the replacement early.
    private val timers = mutableMapOf<String, Job>()

    override fun dismiss(id: String) {
        timers.remove(id)?.cancel()
        _items.value = _items.value.filterNot { it.id == id }
    }

    fun show(style: NotificationType, message: String, description: String?, autoHide: Duration?) {
        @OptIn(ExperimentalAtomicApi::class)
        val n = Notification(
            style, message, description, Uuid.random().toString(),
            autoHide?.inWholeMilliseconds?.toInt()
        )
        show(n)
    }

    override fun show(notification: Notification) {
        val id = notification.id ?: Uuid.random().toString()
        timers.remove(id)?.cancel()
        // Same id replaces in place, keeping its position rather than jumping to the end.
        val replacement = notification.copy(id = id)
        val current = _items.value
        _items.value = if (current.any { it.id == id }) {
            current.map { if (it.id == id) replacement else it }
        } else {
            current + replacement
        }
        val autoDismiss = notification.dismiss?.milliseconds
        if (autoDismiss != null) {
            timers[id] = scope.launch {
                delay(autoDismiss)
                dismiss(id)
            }
        }
    }
}

/**
 * Render notifications.
 */
@Composable
fun Notifications(
    center: NotificationCenter
) {
    // Collect the list directly; no extra timers here.
    val items by center.items.collectAsState()

    Div({ classes(NotificationStyles.container) }) {
        items.forEach { n ->
            NotificationBox(n) {
                n.id?.let {
                    center.dismiss(it)
                }
            }
        }
    }
}

fun notificationStyle(style: NotificationType): String {
    return style.lowercase() + "Box"
}

@Composable
fun NotificationBox(notification: Notification, remove: (() -> Unit)? = null) {
    Div({ classes(NotificationStyles.box, notificationStyle(notification.style)) }) {
        Div({ classes(NotificationStyles.boxMessage) }) { Text(notification.message) }
        notification.description?.let { description ->
            Div({ classes(NotificationStyles.boxMessageContent) }) {
                Text(description)
            }
        }
        if (remove != null) {
            Div({
                classes(NotificationStyles.boxClose)
                onClick { remove() }
            }) {
                svgIcon(SVGIcons.xMark) { size = 18.px }
            }
        }
    }
}


object NotificationStyles : StyleSheet() {
    val container by style {
        display(DisplayStyle.Flex)
        flexDirection(FlexDirection.Column)
        gap(10.px)
        position(Position.Fixed)
        top(40.px)
        left(0.px)
        right(0.px)
        property("z-index", "20")
        padding(5.px)
        alignItems(AlignItems.FlexStart)
    }

    val boxMessage by style {
        fontWeight(700)
    }

    val boxMessageContent by style {
        padding(5.px, 0.px)
    }

    val box by style {
        backgroundColor(SXMUI.messageAlertBackground.value())
        border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
        padding(1.cssRem, 2.cssRem, 1.cssRem, 1.cssRem)
        position(Position.Relative)
        borderRadius(4.px)
        boxShadow("4px 4px 5px rgba(0, 0, 0, 0.1)")


        (self + ".infoBox") style {
            backgroundColor(SXMUI.messageInfoBackground.value())
            border(1.px, LineStyle.Solid, SXMUI.messageInfoBorder.value())
            child(self, className(boxMessage)) style {
                color(SXMUI.messageInfoForeground.value())
            }
        }
        (self + ".errorBox") style {
            backgroundColor(SXMUI.messageAlertBackground.value())
            border(1.px, LineStyle.Solid, SXMUI.messageAlertBorder.value())
            child(self, className(boxMessage)) style {
                color(SXMUI.messageAlertForeground.value())
            }
        }
        (self + ".warningBox") style {
            backgroundColor(SXMUI.messageWarnBackground.value())
            border(1.px, LineStyle.Solid, SXMUI.messageWarnBorder.value())
            child(self, className(boxMessage)) style {
                color(SXMUI.messageWarnForeground.value())
            }
        }
        (self + ".successBox") style {
            backgroundColor(SXMUI.messageSuccessBackground.value())
            border(1.px, LineStyle.Solid, SXMUI.messageSuccessBorder.value())

            child(self, className(boxMessage)) style {
                color(SXMUI.messageSuccessForeground.value())
            }
        }
    }

    val boxClose by style {
        position(Position.Absolute)
        top(5.px)
        right(5.px)
        cursor("pointer")
    }
}
